import {
  AccountBookOutlined,
  AimOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BranchesOutlined,
  BulbOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  ContactsOutlined,
  DashboardOutlined,
  FileTextOutlined,
  FundProjectionScreenOutlined,
  GlobalOutlined,
  HistoryOutlined,
  HomeOutlined,
  ImportOutlined,
  InboxOutlined,
  LineChartOutlined,
  LockOutlined,
  NotificationOutlined,
  PaperClipOutlined,
  PartitionOutlined,
  ProfileOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  StarOutlined,
  TeamOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UserOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { type ComponentType, createElement } from 'react';
import { availableOverviewViews } from '@/modules/zy-crm/utils/overview-navigation';
import type { OverviewEntity } from '@/services/bff/crm-overview.api';
import { LEGACY_PATHS } from './legacyPaths';

const NAV_ICONS = {
  home: HomeOutlined,
  notification: NotificationOutlined,
  bulb: BulbOutlined,
  team: TeamOutlined,
  contacts: ContactsOutlined,
  rise: RiseOutlined,
  fileText: FileTextOutlined,
  shopping: ShoppingOutlined,
  profile: ProfileOutlined,
  accountBook: AccountBookOutlined,
  barChart: BarChartOutlined,
  calendar: CalendarOutlined,
};

import { SYSTEM_CONFIG_GROUPS } from '@/modules/zy-crm/components/system-config/registry';
import type { RuntimeMenuItem } from '@/utils/menuData';
import routes from '../../config/routes';

export const MODULES = [
  { key: 'workbench', path: '/workbench', icon: 'home', label: '工作台' },
  { key: 'marketing', path: '/marketing', icon: 'notification', label: '营销' },
  { key: 'lead', path: '/lead', icon: 'bulb', label: '线索' },
  { key: 'customer', path: '/customer', icon: 'team', label: '客户' },
  { key: 'contact', path: '/contact', icon: 'contacts', label: '联系人' },
  { key: 'opportunity', path: '/opportunity', icon: 'rise', label: '商机' },
  {
    key: 'business-trip',
    path: '/business-trip',
    icon: 'calendar',
    label: '出差',
  },
  { key: 'quote', path: '/quote', icon: 'fileText', label: '报价' },
  { key: 'product', path: '/product', icon: 'shopping', label: '产品' },
  { key: 'contract', path: '/contract', icon: 'profile', label: '合同' },
  { key: 'receipt', path: '/receipt', icon: 'accountBook', label: '回款' },
  { key: 'performance', path: '/performance', icon: 'rise', label: '业绩' },
  { key: 'analysis', path: '/analysis', icon: 'barChart', label: '分析' },
] as const;

type NavigationRoute = {
  path: string;
  name?: string;
  locale?: string;
  access?: string;
  hideInMenu?: boolean;
  component?: string;
};
const pageRoutes = routes as NavigationRoute[];
const PAGE_ORDER: Record<string, string[]> = {
  'business-trip': ['workbench', 'items', 'trip', 'analysis'],
  performance: [
    'overview',
    'targets',
    'entries',
    'splits',
    'team',
    'reconciliation',
  ],
  analysis: [
    'business',
    'customer',
    'sales',
    'performance',
    'product',
    'lead-source',
    'business-map',
    'governance',
  ],
  workbench: [
    'overview',
    'approval',
    'calendar',
    'task',
    'follow-up',
    'attachment',
  ],
  lead: [
    'overview',
    'my',
    'team',
    'collab',
    'pool',
    'all',
    'imported',
    'import-conflict',
  ],
  customer: ['overview', 'my', 'team', 'collab', 'pool', 'all'],
  contact: ['overview', 'my', 'team', 'collab', 'all'],
  opportunity: ['overview', 'my', 'followed', 'team', 'collab', 'pool', 'all'],
  quote: ['my', 'team', 'collab', 'all'],
  contract: ['my', 'team', 'collab', 'all'],
  receipt: ['/receipt/list', '/receipt/schedule', '/invoice/list'],
};
const PAGE_ICONS: Record<string, ComponentType> = {
  overview: HomeOutlined,
  approval: AuditOutlined,
  calendar: CalendarOutlined,
  task: CarryOutOutlined,
  'business-trip': CalendarOutlined,
  'follow-up': HistoryOutlined,
  attachment: PaperClipOutlined,
  my: UserOutlined,
  team: TeamOutlined,
  collab: ShareAltOutlined,
  pool: InboxOutlined,
  all: TeamOutlined,
  imported: ImportOutlined,
  'import-conflict': WarningOutlined,
  followed: StarOutlined,
  list: FileTextOutlined,
  schedule: CalendarOutlined,
  catalog: ShoppingOutlined,
};
const ROUTE_ICONS: Record<string, ComponentType> = {
  '/performance/overview': DashboardOutlined,
  '/performance/targets': AimOutlined,
  '/performance/entries': UnorderedListOutlined,
  '/performance/splits': PartitionOutlined,
  '/performance/team': TeamOutlined,
  '/performance/reconciliation': LockOutlined,
  '/analysis/business': FundProjectionScreenOutlined,
  '/analysis/customer': SolutionOutlined,
  '/analysis/sales': LineChartOutlined,
  '/analysis/performance': TrophyOutlined,
  '/analysis/product': AppstoreOutlined,
  '/analysis/lead-source': BranchesOutlined,
  '/analysis/business-map': GlobalOutlined,
  '/analysis/governance': SafetyCertificateOutlined,
};
export const getModuleKey = (pathname: string) => {
  if (pathname.startsWith('/invoice')) return 'receipt';
  return MODULES.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  )?.key;
};
export const canUseProductCenter = (
  user?: {
    memberUuid?: string;
    tenantUuid?: string;
    organizationUuid?: string;
  } | null,
) => Boolean(user?.memberUuid && user.tenantUuid && user.organizationUuid);

export function getSettingsItems(
  access: Record<string, boolean>,
): RuntimeMenuItem[] {
  return SYSTEM_CONFIG_GROUPS.flatMap((group) => {
    const children = group.items
      .filter((item) =>
        item.key === 'ai-model-binding'
          ? access.modelSettings
          : Boolean(item.resourceCode && access[item.resourceCode]),
      )
      .map((item) => ({
        key: item.path,
        path: item.path,
        name: item.label,
        title: item.label,
        locale: item.localeId,
      }));
    return children.length
      ? [
          {
            key: group.key,
            name: group.label,
            title: group.label,
            locale: group.localeId,
            children,
          },
        ]
      : [];
  });
}

export function buildNavigation(
  access: Record<string, boolean>,
  productEnabled: boolean,
): RuntimeMenuItem[] {
  return MODULES.flatMap((module) => {
    const children = pageRoutes
      .filter(
        (route) =>
          route.component &&
          !route.hideInMenu &&
          getModuleKey(route.path) === module.key &&
          (!route.access ||
            (route.path.endsWith('/overview') &&
            ['lead', 'customer', 'contact', 'opportunity'].includes(module.key)
              ? availableOverviewViews(module.key as OverviewEntity, access)
                  .length > 0
              : access[route.access])) &&
          (module.key !== 'product' || productEnabled),
      )
      .sort((a, b) => {
        const order = PAGE_ORDER[module.key];
        if (!order) return 0;
        const index = (path: string) =>
          order.indexOf(
            module.key === 'receipt' ? path : path.split('/').pop() || '',
          );
        return index(a.path) - index(b.path);
      })
      .map((route) => ({
        key: route.path,
        path: route.path,
        name: route.name,
        locale:
          [
            'lead',
            'customer',
            'contact',
            'opportunity',
            'quote',
            'contract',
          ].includes(module.key) &&
          ['my', 'team', 'collab', 'followed', 'all'].includes(
            route.path.split('/').pop() || '',
          )
            ? `menu.crm.scope.${route.path.split('/').pop()}`
            : module.key === 'workbench' ||
                route.path === '/analysis/lead-source'
              ? `menu.crm.page.${route.path.split('/').pop()}`
              : route.locale,
        icon: createElement(
          route.path.startsWith('/invoice')
            ? WalletOutlined
            : ROUTE_ICONS[route.path] ||
                PAGE_ICONS[route.path.split('/').pop() || ''] ||
                NAV_ICONS[module.icon],
        ),
      }));
    if (!children.length) return [];
    return [
      {
        key: module.path,
        path: module.path,
        name: module.label,
        title: module.label,
        locale: `menu.crm.nav.${module.key}`,
        icon: createElement(NAV_ICONS[module.icon]),
        children,
      },
    ];
  });
}

export function getDefaultPath(
  pathname: string,
  menus: RuntimeMenuItem[],
  settings: RuntimeMenuItem[],
): string | undefined {
  if (pathname.startsWith('/setting'))
    return settings.flatMap((group) => group.children || [])[0]?.path;
  const key = getModuleKey(pathname === '/' ? '/workbench' : pathname);
  const children = menus.find(
    (item) => item.key === MODULES.find((module) => module.key === key)?.path,
  )?.children;
  if (
    (key === 'lead' || key === 'customer' || key === 'opportunity') &&
    children?.some((item) => item.path === `/${key}/my`)
  ) {
    return `/${key}/my`;
  }
  return children?.[0]?.path;
}

export function getSourceListPath(
  pathname: string,
  menus: RuntimeMenuItem[],
  sourcePath?: string,
): string | undefined {
  const module = MODULES.find((item) => item.key === getModuleKey(pathname));
  if (!module) return undefined;
  const visible =
    menus.find((item) => item.key === module.path)?.children || [];
  const exact = visible.find((item) => item.path === pathname);
  if (exact?.path) return exact.path;
  if (sourcePath?.startsWith('/') && !sourcePath.startsWith('//')) {
    const base = sourcePath.split(/[?#]/)[0];
    const canonical = LEGACY_PATHS[base] || base;
    if (visible.some((item) => item.path === canonical))
      return canonical + sourcePath.slice(base.length);
  }
  const detail = (
    routes as Array<NavigationRoute & { parentKeys?: string[] }>
  ).find(
    (route) =>
      route.hideInMenu &&
      new RegExp(`^${route.path.replace(/:[^/]+/g, '[^/]+')}$`).test(pathname),
  );
  return (
    detail?.parentKeys?.find((key) =>
      visible.some((item) => item.path === key),
    ) || visible[0]?.path
  );
}
export function getSelectedMenuKeys(
  pathname: string,
  menus: RuntimeMenuItem[],
  sourcePath?: string,
): string[] {
  const module = MODULES.find((item) => item.key === getModuleKey(pathname));
  if (!module)
    return pathname.startsWith('/setting') ? ['/setting', pathname] : [];
  const parent = getSourceListPath(pathname, menus, sourcePath)?.split(
    /[?#]/,
  )[0];
  return [module.path, ...(parent ? [parent] : [])];
}
